import React, { useEffect, useState } from 'react';
import { CustomerSwitchHeader } from '../../../constants/table-headers/customerSwitchHeader';
import Table from '../../../components/table';
import {
  useAdvanceBoolean,
  useBoolean,
  useGetCurrentTenantInfo,
  useGetCustomerSuccessCompanies,
  useGetSwitchSandboxList,
  useGetTablePreferences,
  usePostTablePreferences,
  useWealthlaneCustomerApi,
} from '../../../hooks';
import { ModalStatusType, TableParamsType } from '../../../types/common';
import InitialValuesForm from '../../../constants/initialValues';
import { PageHeader } from '../../../constants/style/common.styled';
import Heading from '../../../components/typography';
import Search from '../../../components/search/search';
import { CustomerSuccessService } from '../../../services/wealthlane-customer-services';
import { setSwitchCredentials } from '../../configuration/customerSwitchUserSlice';
import { useDispatch } from 'react-redux';
import AlertPopup from '../../../components/alert-popup';
import Button from '../../../components/button';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import SwitchSandboxModal from './switchSandboxModal';
import { SwitchSandboxHeader } from '../../../constants/table-headers/switchSandboxHeader';
import {
  InstanceSwitchService,
  SandboxService,
} from '../../../services/wealthlane-identity-services';
import useDeleteSandbox from '../../../hooks/delete/useDeleteSandbox';
import useInstance from '../../../hooks/custom-hooks/useInstance';
import {
  DynamicTable,
  withDynamicTableContext,
  useDynamicTable,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import Spinners from '../../../components/spinner';
import { axiosInstance } from '../../../services/apiInterceptor';
import PushToProductionModal from '../push-to-production/pushToProductionModal';
import permissionCheck from '../../../utils/permission/permissionCheck';

const initialValues = {
  data: InitialValuesForm.switchSandboxModal,
  id: '',
};

const SwitchSandbox = () => {
  const dispatch = useDispatch();
  const { handleRequest } = useWealthlaneCustomerApi();
  // const [tableInfo, setTableInfo] = useState<TableParamsType>(InitialValuesForm.tableParams);
  const { tableInfo, setTableInfo } = useDynamicTable();

  useEffect(() => {
    setTableInfo(InitialValuesForm.tableParams);
  }, []);

  const {
    value: isDeleteModalOpen,
    setFalse: closeDeleteModal,
    setTrue: openDeleteModal,
    customValue: deleteId,
    setCustomValue: setDeleteId,
  } = useAdvanceBoolean<string>(false);

  const {
    data: switchSandboxList,
    isLoading,
    status,
  } = useGetSwitchSandboxList(handleRequest, tableInfo);
  const { mutateAsync: deleteSandbox, isLoading: isDeleting } = useDeleteSandbox(
    handleRequest,
    closeDeleteModal
  );
  const { data: currentTenant } = useGetCurrentTenantInfo(handleRequest);

  const [searchQuery, setSearchQuery] = useState('');
  const {
    value: isConfirmOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
    customValue,
    setCustomValue,
  } = useAdvanceBoolean<string>(false);
  const [loading, setLoading] = useState(false);
  const [isForceEnableSubmit, setIsForceEnableSubmit] = useState(false);

  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const {
    value: isPushToProductionModalOpen,
    setFalse: closePushToProductionModal,
    setTrue: openPushToProductionModal,
  } = useBoolean(false);
  const [modalStatus, setModalStatus] = useState<ModalStatusType>('Add');
  const [initialData, setInitialData] = useState(initialValues);
  const [rowData, setRowData] = useState<any>();
  const { onKillInstance } = useInstance({ onClose() {} });
  const [headerList, setHeaderList] = useState<string[]>([]);

  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'SANDBOX_TABLE',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });
  const data = React.useMemo(() => switchSandboxList?.items || [], [switchSandboxList, isLoading]);
  const { data: tablePreferences } = useGetTablePreferences(handleRequest, 'Sandbox');
  const hasLoadToMainPermission = permissionCheck({
    type: 'Create',
    permission: 'Production',
    feature: 'Customer',
  });

  useEffect(() => {
    tablePreferences != null && setHeaderList([...tablePreferences]);
  }, [tablePreferences]);

  const { mutate: postTablePreference, isLoading: tablePrefLoading } =
    usePostTablePreferences(handleRequest);

  const handleSavePreference = (value: string[]): void => {
    const prefPayload = {
      tableName: 'Sandbox',
      preferences: value,
    };
    postTablePreference(prefPayload);
  };

  // const onSwitch = (id: string) => {
  //   setLoading(true);
  //   handleRequest(InstanceSwitchService.getApiIdentityApiInstanceSwitch(id))
  //     .then((response: any) => {
  //       if (response != null) {
  //         dispatch(
  //           setSwitchCredentials({
  //             tenantId: response?.token,
  //             isOrigin: '',
  //           })
  //         );
  //         window?.localStorage?.setItem('tenantId', response?.token);
  //         window?.localStorage?.setItem('isOrigin', response?.isOrigin as string);

  //         closeConfirmModal();
  //         window.location.reload();
  //         setLoading(false);
  //       }
  //     })
  //     .catch((error) => {
  //       console.log('error', error);
  //       setLoading(false);
  //     })
  //     .finally(() => {
  //       setLoading(false);
  //       closeConfirmModal();
  //     });
  // };

  const onDeleteSandbox = async () => {
    try {
      await deleteSandbox(deleteId);
      if (currentTenant?.id === rowData?.tenantId) {
        onKillInstance();
      }
    } catch (error) {}
  };

  const actions = [
    // {
    //   action: 'Switch',
    //   onClick: (id: string, rowData: any) => {
    //     openConfirmModal();
    //     setRowData(rowData);
    //     localStorage?.setItem('sandbox', rowData?.tenantId);
    //     localStorage?.setItem('sandboxName', rowData?.name);
    //     setCustomValue(id);
    //   },
    // },
    ...(hasLoadToMainPermission
      ? [
          {
            id: 'loadToMainAction',
            action: 'Load to Main',
            onClick: (id: string, rowData: any) => {
              setIsForceEnableSubmit(true);
              setRowData(rowData);
              openPushToProductionModal();
            },
          },
        ]
      : []),
    {
      id: 'editAction',
      action: 'Edit',
      as: 'Edit',
      onClick: (id: string, rowData: any) => {
        setModalStatus('Edit');
        setInitialData({ data: rowData, id: id });
        setRowData(rowData);
        openModal();
      },
    },
    {
      id: 'deleteAction',
      action: <div className='delete-label'>Delete</div>,
      as: 'Delete',
      onClick: (id: string, rowData: any) => {
        setDeleteId(rowData.id);
        setRowData(rowData);
        openDeleteModal();
      },
    },
  ];

  // const onPrimaryClick = (values: Record<string, any>): void => {
  //   // onSwitch(customValue);
  // };

  const renderRightToolbarButton = () => {
    return (
      <span className='flex space-x-2'>
        {hasLoadToMainPermission && (
          <Button
            id='loadToMainButton'
            as='Create'
            variant={'primary-outline'}
            title={'Load To Main'}
            onClick={() => {
              setModalStatus('Add');
              openPushToProductionModal();
            }}
            icon='add_circle'
          />
        )}
        <Button
          as='Create'
          variant={'primary'}
          title={'Add Sandbox'}
          onClick={() => {
            setModalStatus('Add');
            setInitialData(initialValues);
            setRowData([]);
            openModal();
          }}
          icon='add_circle'
          id='addSandboxButton'
        />
      </span>
    );
  };
  const tableHeaderList = SwitchSandboxHeader({
    actions: schemaData?.columns,
    tableActions: actions,
  })
    .filter((item) => item.isSubHeader)
    .map((item) => {
      // console.log('item', item);

      return {
        label: item.Header,
        value: item.accessor,
      };
    });

  const sandboxHeader = (): any[] => {
    return SwitchSandboxHeader({ actions: schemaData?.columns, tableActions: actions }).filter(
      (item) => item.isSubHeader === false || headerList.includes(item.accessor)
    );
  };

  const schema = React.useMemo(
    () => ({
      columns: sandboxHeader(),
      options: schemaData?.options as any,
    }),
    [schemaData]
  );

  if (isTableSchemaLoading) return <Spinners containerClassName='h-screen' id='loadingSpinner' />;

  return (
    <div data-page='sandbox' id='pageContainer'>
      <PageHeader id='pageHeader'>
        <Heading variant='h3' title='Sandbox' id='pageTitle' />
        <Heading variant='body-s' title='List of sandbox.' id='pageSubtitle' />
      </PageHeader>
      {/* <div className='grid lg:grid-cols-12 gap-4 lg:gap-1.5 items-start mb-8'>
        <div className='lg:col-span-3'>
          <Search
            placeholder='Search'
            searchValue={searchQuery}
            onSearch={(value: string) => {
              setSearchQuery(value);
            }}
            setTableInfo={setTableInfo}
            tableInfo={tableInfo}
          />
        </div>
        <div className='lg:col-span-9 justify-items-end'>
          <Button
            as='Create'
            variant={'primary'}
            title={'Add Sandbox'}
            onClick={() => {
              setModalStatus('Add');
              setInitialData(initialValues);
              setRowData([]);
              openModal();
            }}
            icon='add_circle'
          />
        </div>
      </div> */}

      {/* {isConfirmOpen && (
        <AlertPopup
          isShow={isConfirmOpen}
          closeModal={closeConfirmModal}
          // modalStatus={modalStatus}
          // title={'this System User'}
          customTitle={`You are about to change to ${rowData?.name} instance. Are you sure?`}
          handleSubmit={onPrimaryClick}
          // setRemark={setRemark}
          // remark={remark}
          loading={loading}
        />
      )} */}
      {isDeleteModalOpen && (
        <AlertPopup
          isShow={isDeleteModalOpen}
          closeModal={closeDeleteModal}
          customTitle={`You are about to delete ${rowData?.name} instance. Are you sure?`}
          handleSubmit={onDeleteSandbox}
          loading={isDeleting}
          id='deleteConfirmModal'
        />
      )}
      <AnimationWrapper id='modalAnimationWrapper'>
        {isModalOpen && (
          <SwitchSandboxModal
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            modalStatus={modalStatus}
            // modalLoading={modalLoading}
            initialData={initialData}
            setModalStatus={setModalStatus}
            rowData={rowData}
            domId='switchSandboxModal'
          />
        )}
        {isPushToProductionModalOpen && (
          <PushToProductionModal
            isModalOpen={isPushToProductionModalOpen}
            closeModal={() => {
              setIsForceEnableSubmit(false);
              setRowData({});
              closePushToProductionModal();
            }}
            modalStatus={modalStatus}
            setModalStatus={setModalStatus}
            rowData={rowData}
            forceEnableSubmit={isForceEnableSubmit}
          />
        )}
      </AnimationWrapper>
      {/* <Table
        columns={SwitchSandboxHeader(actions)}
        data={switchSandboxList?.items ?? []}
        loading={status}
        showPagination={true}
        totalCounts={switchSandboxList?.totalCount ?? 10}
        tableInfo={tableInfo}
        setTableInfo={setTableInfo}
      /> */}
      <DynamicTable
        schema={schema}
        data={data}
        totalCounts={switchSandboxList?.totalCount ?? 10}
        loading={status}
        components={{
          renderRightToolbarButton,
        }}
        tableColumnList={tableHeaderList}
        selectedColumnList={headerList}
        tablePreferenceLoading={tablePrefLoading}
        handleColumnSave={handleSavePreference}
        id='dataTable'
      />
    </div>
  );
};

export default withDynamicTableContext(SwitchSandbox, false, axiosInstance);
